<template>
  <div class="ai-cid-suggestions" v-if="aiEnabled && (loading || suggestions.length > 0)">
    <div class="ai-cid-suggestions-loading" v-if="loading">
      <div class="loading" />
      <span>Lendo a anamnese...</span>
    </div>

    <template v-else>
      <span class="ai-cid-suggestions-label">
        <fa-icon :icon="['fal', 'sparkles']" /> Sugestões
      </span>

      <button
        type="button"
        class="ai-cid-suggestion tooltip"
        v-for="(item, i) in suggestions"
        :key="i"
        :data-tooltip="tooltip(item)"
        @click="select(item)"
      >
        <fa-icon :icon="['fal', 'plus-circle']" />
        <span>{{ item.code }} - {{ shorten(item.name) }}</span>
      </button>

      <button type="button" class="btn btn-sm btn-link" @click="clear">Fechar</button>

      <span class="ai-cid-suggestions-disclaimer">
        Sugestão por IA a partir do que está escrito na anamnese. Confira antes de registrar.
      </span>
    </template>
  </div>
</template>

<script>
import { toText } from '@/helpers/html';
import ai from '@/mixins/ai';

const MAX_NAME_LENGTH = 46;

export default {
  mixins: [ai],
  props: {
    medicalRecordId: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      default: '',
    },
    cids: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
      items: [],
    };
  },
  computed: {
    content() {
      return toText(this.text).trim();
    },
    registered() {
      return this.cids.filter(cid => cid && cid.code).map(cid => cid.code);
    },
    suggestions() {
      return this.items.filter(item => !this.registered.includes(item.code));
    },
  },
  methods: {
    load() {
      if (!this.medicalRecordId || this.loading) {
        return Promise.resolve();
      }

      this.loading = true;
      this.$emit('loading', true);

      return this.$httpX
        .post(`/medical-records/${this.medicalRecordId}/assistant/cid-suggest`, {
          text: this.content,
        })
        .then(({ data }) => {
          this.items = data.suggestions || [];

          if (this.suggestions.length === 0) {
            this.$toast.show(
              data.enabled === false
                ? 'Sugestão de CID indisponível no momento.'
                : 'Nenhum CID sugerido para o que está escrito na anamnese.',
              { type: 'warning' },
            );
          }
        })
        .catch(() => {
          this.items = [];
          this.$toast.show('Não foi possível gerar sugestões de CID.', { type: 'error' });
        })
        .finally(() => {
          this.loading = false;
          this.$emit('loading', false);
        });
    },
    clear() {
      this.items = [];
    },
    tooltip({ evidence, reason }) {
      return [evidence ? `"${evidence}"` : '', reason].filter(Boolean).join(' — ');
    },
    shorten(name) {
      const value = name || '';
      return value.length > MAX_NAME_LENGTH
        ? `${value.substring(0, MAX_NAME_LENGTH)}...`
        : value;
    },
    select(item) {
      this.$emit('select', { code: item.code, name: item.name });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";

.ai-cid-suggestions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: $layout-spacing;
  margin-bottom: $layout-spacing-lg;

  .ai-cid-suggestions-loading {
    align-items: center;
    color: $gray-color-700;
    display: flex;
    font-size: $font-size-sm;
    gap: $layout-spacing-lg;

    .loading {
      flex: 0 0 auto;
      margin: 0;
      width: $unit-4;
    }
  }

  .ai-cid-suggestions-label {
    color: $purple-neon-color-500;
    font-size: $font-size-sm;
    font-weight: 600;
    text-transform: uppercase;
    white-space: nowrap;

    svg {
      margin-right: $layout-spacing-sm;
    }
  }

  .ai-cid-suggestion {
    align-items: center;
    background-color: $light-color;
    border: $border-width dashed $purple-neon-color-500;
    border-radius: $border-radius;
    color: $gray-color-900;
    cursor: pointer;
    display: flex;
    font-size: $font-size-sm;
    gap: $layout-spacing-sm;
    padding: 0 $layout-spacing;

    svg {
      color: $purple-neon-color-500;
    }

    &:hover {
      background-color: $gray-color-ultra-light;
    }
  }

  .ai-cid-suggestions-disclaimer {
    color: $gray-color-600;
    font-size: $font-size-xs;
    width: 100%;
  }
}
</style>
